<template>
  <div>
    <Mouse>
      <Header></Header>
      <TransitionBars />
      <main :class="{ blur: menuIsOpen }">
        <router-view />
      </main>
      <Footer />
      <transition name="fade">
        <Menu v-if="menuIsOpen" />
      </transition>
      <transition name="fade">
        <Video v-if="showVideo" />
      </transition>
    </Mouse>
  </div>
</template>

<script>
import anime from "animejs";
import router from "./router";
import store from "./store";

import Mouse from "./components/Mouse.vue";
import Header from "./components/Header.vue";
import Footer from "./components/Footer.vue";
import Menu from "./components/Menu.vue";
import Video from "./components/Video.vue";
import TransitionBars from "./components/TransitionBars.vue";

router.beforeEach((to, from, next) => {
  setTimeout(() => {
    next();
  }, 1400);
  store.commit("toggleBars");
});

router.afterEach((to, from) => {
  store.dispatch("getContent", to.name).then(response => {
    store.commit("toggleBars");
    anime({
      targets: ".title span",
      translateY: ["1.1em", 0],
      translateZ: 0,
      duration: 2000,
      easing: "easeInOutCubic",
      delay: 200
    });
    anime({
      targets: ".footer__smLink",
      opacity: [0, 1],
      duration: 1000,
      easing: "easeInOutCubic",
      delay: function (el, i) {
        return 300 * i + 5000;
      }
    });
    if (to.name === "home")
      document.querySelector(".footer").classList.add("footerInHome");
    else if (to.name === "works") {
      const videoId = location.hash.split("?")[1];
      if (videoId) store.commit("openVideo", { group: 'works', id: videoId });
    } else if (to.name === "worksLongFormat") {
      const videoId = location.hash.split("?")[1];
      if (videoId) store.commit("openVideo", { group: 'worksLongFormat', id: videoId });
    } document.querySelector(".footer").classList.remove("footerInHome");
  });
});

export default {
  name: "App",
  components: {
    Mouse,
    Header,
    Footer,
    TransitionBars,
    Menu,
    Video
  },
  computed: {
    menuIsOpen() {
      return this.$store.state.showMenu;
    },
    showVideo() {
      return this.$store.state.showVideo;
    }
  }
};
</script>

<style lang="scss">
@mixin mq($smallerValue, $biggerValue) {
  @media (min-width: #{$smallerValue}) and (max-width: $biggerValue) {
    @content;
  }
}

:root {
  --Neu01: #ffffff;
  --Neu02: #f4f4f4;
  --Neu03: #ebebeb;
  --Neu04: #cccccc;
  --Neu05: #afafaf;
  --Neu06: #999999;
  --Neu07: #666666;
  --Neu08: #4a4d4e;
  --Neu09: #333333;
  --Neu10: #19171a;
  --Neu11: #000000;

  --M01: #f33535;
  --M02: #769f3d;
}

html {
  scroll-behavior: smooth;
}

body {
  background-color: var(--Neu10);
  cursor: url("assets/dot.svg") 4 4, auto;
  text-align: center;
  overflow-x: hidden;
}

@font-face {
  font-family: "CamptonBold";
  src: url("assets/CamptonBold.otf");
  font-weight: 700;
  font-style: normal;
}

@font-face {
  font-family: "CamptonBook";
  src: url("assets/CamptonBook.otf");
  font-weight: 600;
  font-style: normal;
}

section {
  width: 100%;
  margin: 0 auto;
  padding: 180px 20px 140px;
  text-align: left;

  @include mq(0, 768px) {
    padding: 100px 20px 40px;
  }
}

.title {
  font-family: CamptonBold;
  font-size: 90px;
  letter-spacing: 1px;
  line-height: 1.3;
  max-width: 1000px;
  margin: 0 auto;
  overflow: hidden;
  color: transparent;
  -webkit-text-stroke-width: 1px;
  -webkit-text-stroke-color: var(--Neu01);

  span {
    display: inline-block;
  }

  em {
    color: var(--Neu01);
  }

  @include mq(0, 768px) {
    font-size: 72px;
    line-height: 1.2;
  }

  @include mq(0, 380px) {
    font-size: 55px;
    line-height: 1.1;
  }
}

.hover {
  padding: 10px;
}

::-webkit-input-placeholder {
  /* Chrome/Opera/Safari */
  color: var(--Neu06);
}

::-moz-placeholder {
  /* Firefox 19+ */
  color: var(--Neu06);
}

:-ms-input-placeholder {
  /* IE 10+ */
  color: var(--Neu06);
}

:-moz-placeholder {
  /* Firefox 18- */
  color: var(--Neu06);
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus textarea:-webkit-autofill,
textarea:-webkit-autofill:hover textarea:-webkit-autofill:focus,
select:-webkit-autofill,
select:-webkit-autofill:hover,
select:-webkit-autofill:focus {
  -webkit-text-fill-color: var(--Neu01);
  -webkit-box-shadow: 0 0 0 30px var(--Neu10) inset;
}

::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: var(--M01);
  opacity: 0.7;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  opacity: 1;
}

.blur {
  filter: blur(5px);
}

// transitions
.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s;
}

.fade-enter,
.fade-leave-to

/* .fade-leave-active below version 2.1.8 */
  {
  opacity: 0;
  //transform: scale(.9);
}

.fadeIn-enter-active {
  transition: all 0.5s ease-in 0.2s;
}

.fadeIn-leave-active {
  transition: all 0.2s ease-in;
}

.fadeIn-enter,
.fadeIn-leave-to

/* .fade-leave-active below version 2.1.8 */
  {
  opacity: 0;
}

.slideUp-enter-active,
.slideUp-leave-active {
  transition: all 0.5s;
}

.slideUp-enter {
  opacity: 0;
  transform: translateY(-20px);
}

.slideUp-leave-to

/* .fade-leave-active below version 2.1.8 */
  {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/App.vue